import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createLocalScreenTracks,
  LocalVideoTrack,
  Room,
  RoomEvent,
  Track,
  type LocalTrack,
  type LocalTrackPublication,
} from 'livekit-client';
import {
  captureOptions,
  DEFAULT_QUALITY,
  enforceContentHint,
  getQuality,
  publishOptions,
  QUALITIES,
  type QualityId,
} from './encoding';
import { ScreenOff, ScreenShare } from './icons';

export function ShareButton({ room }: { room: Room }) {
  const [sharing, setSharing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qualityId, setQualityId] = useState<QualityId>(
    () => (localStorage.getItem('tela.quality') as QualityId) || DEFAULT_QUALITY,
  );
  const published = useRef<LocalTrack[]>([]);

  /*
   * Uma fonte de verdade so: o evento do proprio SDK.
   *
   * Antes havia um ouvinte de 'ended' na faixa nativa que tambem chamava stop().
   * So que o SDK ja despublica sozinho quando a captura termina
   * (`handleTrackEnded`), e o nosso `unpublishTrack(t, true)` para a captura —
   * o que dispara 'ended'. Resultado: dois caminhos despublicando a mesma coisa,
   * um deles em resposta ao outro.
   *
   * Escutando LocalTrackUnpublished, o botao reflete o estado real seja qual for
   * a origem: clique aqui, botao "parar de compartilhar" do navegador, ou queda
   * de conexao.
   */
  useEffect(() => {
    const aoDespublicar = (pub: LocalTrackPublication) => {
      if (pub.source !== Track.Source.ScreenShare) return;
      published.current = published.current.filter((t) => t !== pub.track);
      setSharing(false);
    };
    room.on(RoomEvent.LocalTrackUnpublished, aoDespublicar);
    return () => {
      room.off(RoomEvent.LocalTrackUnpublished, aoDespublicar);
    };
  }, [room]);

  const stop = useCallback(async () => {
    const tracks = published.current;
    published.current = [];
    for (const t of tracks) {
      await room.localParticipant.unpublishTrack(t, true).catch(() => {});
    }
    setSharing(false);
  }, [room]);

  const start = useCallback(
    async (id: QualityId) => {
      setBusy(true);
      setError(null);
      const q = getQuality(id);
      try {
        const tracks = await createLocalScreenTracks(captureOptions(q));

        for (const track of tracks) {
          if (track.kind === Track.Kind.Video) {
            enforceContentHint(track as LocalVideoTrack, q);
          }
          await room.localParticipant.publishTrack(track, publishOptions(q));
        }

        published.current = tracks;
        setSharing(true);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        // Cancelar o seletor do navegador nao e erro.
        if (!/Permission denied|NotAllowedError|cancel/i.test(msg)) {
          setError(msg);
        }
      } finally {
        setBusy(false);
      }
    },
    [room],
  );

  // Trocar a qualidade durante o compartilhamento: republica com o novo preset.
  const changeQuality = useCallback(
    async (id: QualityId) => {
      setQualityId(id);
      localStorage.setItem('tela.quality', id);
      if (sharing) {
        await stop();
        await start(id);
      }
    },
    [sharing, start, stop],
  );

  /*
   * Nao ha limpeza no desmonte de proposito.
   *
   * Um `stop()` no desmonte parece prudente, mas dispara a cada re-montagem do
   * componente — inclusive as que o React faz por conta propria — e derruba uma
   * transmissao que ninguem pediu para parar. Quem desconecta a sala ja encerra
   * as faixas locais; nao precisamos duplicar isso aqui.
   */

  return (
    <div className="share">
      {error && <span className="error">{error}</span>}

      <label className="quality">
        <select
          value={qualityId}
          aria-label="Qualidade da transmissao"
          onChange={(e) => void changeQuality(e.target.value as QualityId)}
        >
          {QUALITIES.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      <button
        className={sharing ? 'btn btn-danger' : 'btn btn-primary'}
        disabled={busy}
        onClick={() => (sharing ? void stop() : void start(qualityId))}
      >
        {sharing ? <ScreenOff /> : <ScreenShare />}
        {busy ? 'Aguarde' : sharing ? 'Parar' : 'Compartilhar tela'}
      </button>
    </div>
  );
}
